export default class SoundManager {
	constructor() {
		this.audioCtx = new AudioContext();
		this._compressorBypassed = false;
		this._volume = 1.0;
		this.normalize = false;

		this.panNode = this.audioCtx.createStereoPanner();
		this.gainNode = this.audioCtx.createGain();
		this.compressorNode = this.audioCtx.createDynamicsCompressor();

		this.gainNode.gain.setValueAtTime(this._volume, this.audioCtx.currentTime);
		this.compressorNode.threshold.setValueAtTime(-50, this.audioCtx.currentTime);
		this.compressorNode.knee.setValueAtTime(40, this.audioCtx.currentTime);
		this.compressorNode.ratio.setValueAtTime(12, this.audioCtx.currentTime);
		this.compressorNode.attack.setValueAtTime(0, this.audioCtx.currentTime);
		this.compressorNode.release.setValueAtTime(0.25, this.audioCtx.currentTime);

		this.panNode.connect(this.gainNode);
		this.gainNode.connect(this.compressorNode);
		this.compressorNode.connect(this.audioCtx.destination);

		this.targetNode = this.panNode;

		this._resumeHandler = () => this.audioCtx.state === 'suspended' && this.audioCtx.resume();
		document.addEventListener('click', this._resumeHandler);
	}

	setGain(value) {
		this.gainNode.gain.setTargetAtTime(value, this.audioCtx.currentTime, 0.01);
	}

	// volume: 0-2 where 1.0 = 100%
	setVolume(volume) {
		this._volume = Math.max(0, volume);
		this.setGain(this._volume);
	}

	// Compute a gain factor that brings a buffer's peak to targetPeak.
	// Capped at maxBoost to avoid extreme amplification of near-silence.
	computeNormalizeGain(buffer, targetPeak = 0.3, maxBoost = 4.0) {
		let peak = 0;
		for(let ch = 0; ch < buffer.numberOfChannels; ch++) {
			const data = buffer.getChannelData(ch);
			for(let i = 0; i < data.length; i++) {
				const abs = Math.abs(data[i]);
				if(abs > peak) peak = abs;
			}
		}
		if(peak < 0.001) return 1;
		return Math.min(targetPeak / peak, maxBoost);
	}

	setCompressor({ enabled, threshold = -50, knee = 40, ratio = 12, attack = 0, release = 250 } = {}) {
		const t = this.audioCtx.currentTime;

		if(enabled !== undefined && enabled !== !this._compressorBypassed) {
			this._compressorBypassed = !enabled;
			if(!enabled) {
				try { this.gainNode.disconnect(this.compressorNode); } catch(e) { /* already disconnected */ }
				try { this.gainNode.connect(this.audioCtx.destination); } catch(e) { /* already connected */ }
			} else {
				try { this.gainNode.disconnect(this.audioCtx.destination); } catch(e) { /* already disconnected */ }
				try { this.gainNode.connect(this.compressorNode); } catch(e) { /* already connected */ }
			}
		}

		this.compressorNode.threshold.setTargetAtTime(threshold, t, 0.01);
		this.compressorNode.knee.setTargetAtTime(knee, t, 0.01);
		this.compressorNode.ratio.setTargetAtTime(ratio, t, 0.01);
		this.compressorNode.attack.setTargetAtTime(attack / 1000, t, 0.01);
		this.compressorNode.release.setTargetAtTime(release / 1000, t, 0.01);
	}

	destroy() {
		document.removeEventListener('click', this._resumeHandler);
		this.audioCtx.close();
	}
}
